import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { parseFloatToFixed } from "../../../../extras";
import {
  IconButtonBackgroundColored,
  IconButtonBackgroundLightGrey,
} from "../../../styled/button";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ScaleIcon from "@mui/icons-material/Scale";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { alpha, styled } from "@mui/material/styles";
import {
  decreaseProductQuantity,
  increaseProductQuantity,
  removeProductFromCart,
  updateCartProductQuantity,
} from "../../../../store/actions/newOrderSlice";
import CustomConfirmationDialog from "../../../dialogs/CustomConfirmationDialog";
import { formatFinance } from "../../../../utils/formatters";

// Styled components
const WeightEditContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(orange[50], 0.3),
  border: `1px solid ${alpha(orange[200], 0.5)}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5),
  margin: theme.spacing(1, 0),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const QuantityContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: alpha(blue[50], 0.3),
  border: `1px solid ${alpha(blue[200], 0.5)}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  margin: theme.spacing(0.5, 0),
}));

// Utility functions
const formatBrazilianNumber = (value) => {
  if (!value) return "";
  return value.toString().replace(".", ",");
};

const parseBrazilianNumber = (value) => {
  if (!value) return 0;
  return parseFloat(value.replace(",", ".")) || 0;
};

const getWeightLabel = (weight) => {
  if (weight >= 1) return "Kg";
  return "g";
};

const formatWeightDisplay = (weight) => {
  return formatBrazilianNumber(weight.toFixed(3));
};

const ProductListItem = ({ product, isFirst }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditingWeight, setIsEditingWeight] = useState(false);
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState("");

  const isWeightBased = product.unit === "KG";
  const currentWeight = product.quantity;
  const weightLabel = getWeightLabel(currentWeight);

  const handleDelete = () => {
    dispatch(removeProductFromCart(product.product_identifier));
  };

  const handleEditWeight = () => {
    setWeightInput(formatWeightDisplay(currentWeight));
    setIsEditingWeight(true);
    setWeightError("");
  };

  const handleWeightChange = (e) => {
    const value = e.target.value;

    // Allow only numbers, comma, and single decimal point
    const validInput = /^\d*,?\d*$/.test(value);

    if (!validInput) return;

    setWeightInput(value);
    setWeightError("");
  };

  const handleWeightSave = () => {
    const numericValue = parseBrazilianNumber(weightInput);

    if (numericValue <= 0) {
      setWeightError("Peso deve ser maior que 0");
      return;
    }

    if (numericValue > 50) {
      setWeightError("Peso máximo permitido é 50kg");
      return;
    }

    // USE THE NEW ACTION TO UPDATE THE QUANTITY
    dispatch(
      updateCartProductQuantity({
        productIdentifier: product.product_identifier,
        quantity: numericValue,
      })
    );

    setIsEditingWeight(false);
    setWeightError("");
  };

  const handleWeightCancel = () => {
    setIsEditingWeight(false);
    setWeightInput("");
    setWeightError("");
  };

  const renderQuantityControls = () => {
    if (isWeightBased) {
      return (
        <Box sx={{ width: "100%" }}>
          {/* Weight Display/Edit */}
          {!isEditingWeight ? (
            <QuantityContainer>
              <ScaleIcon sx={{ color: orange[600], mr: 1 }} />
              <Box sx={{ textAlign: "center", flexGrow: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: orange[800] }}
                >
                  {formatWeightDisplay(currentWeight)} {weightLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFinance(
                    parseFloatToFixed(product.total_price * product.quantity)
                  )}
                </Typography>
              </Box>
              <IconButtonBackgroundLightGrey
                padding={1.5}
                onClick={handleEditWeight}
              >
                <EditIcon fontSize="small" />
              </IconButtonBackgroundLightGrey>
            </QuantityContainer>
          ) : (
            <WeightEditContainer>
              <TextField
                size="small"
                value={weightInput}
                onChange={handleWeightChange}
                error={!!weightError}
                helperText={weightError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: orange[700] }}
                      >
                        {getWeightLabel(parseBrazilianNumber(weightInput))}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flexGrow: 1,
                  mr: 1,
                  ".MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />
              <IconButtonBackgroundColored
                color={green[600]}
                padding={1.5}
                onClick={handleWeightSave}
                disabled={!!weightError || !weightInput}
              >
                <CheckIcon fontSize="small" />
              </IconButtonBackgroundColored>
              <IconButtonBackgroundLightGrey
                padding={1.5}
                onClick={handleWeightCancel}
              >
                <CloseIcon fontSize="small" />
              </IconButtonBackgroundLightGrey>
            </WeightEditContainer>
          )}
        </Box>
      );
    }

    // Regular quantity controls for non-weight products
    return (
      <QuantityContainer>
        <IconButtonBackgroundLightGrey
          padding={2}
          onClick={() =>
            dispatch(decreaseProductQuantity(product.product_identifier))
          }
        >
          <RemoveCircleIcon />
        </IconButtonBackgroundLightGrey>
        <Stack sx={{ mx: 1, textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{ minWidth: "50px", fontWeight: 600 }}
          >
            {product.quantity}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFinance(
              parseFloatToFixed(product.total_price * product.quantity)
            )}
          </Typography>
        </Stack>
        <IconButtonBackgroundLightGrey
          padding={2}
          onClick={() =>
            dispatch(increaseProductQuantity(product.product_identifier))
          }
        >
          <AddCircleIcon />
        </IconButtonBackgroundLightGrey>
      </QuantityContainer>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20, height: 0 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      style={{ overflow: "hidden" }}
      layout
    >
      <ListItem
        sx={{
          pb: 2,
          px: 2,
          mb: 0,
          backgroundColor: "#fff",
          borderBottom: "1px solid" + grey["300"],
          borderTopRightRadius: isFirst ? 24 : 0,
          borderTopLeftRadius: isFirst ? 24 : 0,
          width: "100%",
          flexWrap: "wrap",
          gap: 1,
          position: "relative",
        }}
      >
        {/* Product Header */}
        <Box sx={{ textAlign: "start", flexGrow: 1, width: "100%" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ mr: 1 }}>
                {product.title}
              </Typography>
              {isWeightBased && (
                <Chip
                  icon={<ScaleIcon />}
                  label="Por peso"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: grey[800] }}
            >
              {formatFinance(product.total_price * product.quantity)}
            </Typography>
          </Box>

          {/* Product Complements */}
          {product.complements && product.complements.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 1, width: "100%" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Adicionais:
                </Typography>
                {product.complements.map((complement) => (
                  <Box
                    key={complement.id}
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                      pl: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mr: 1 }}
                    >
                      {complement.quantity}x {complement.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFinance(complement.price * complement.quantity)}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
            </>
          )}
        </Box>

        {/* Quantity Controls */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderQuantityControls()}
        </Box>

        {/* Delete Button */}
        <IconButtonBackgroundColored
          padding={2}
          color={red[600]}
          onClick={() => setOpenDialog(true)}
          sx={{
            position: "absolute",
            left: 12,
            bottom: 12,
            "&:hover": {
              backgroundColor: alpha(red[50], 0.8),
            },
          }}
        >
          <DeleteIcon />
        </IconButtonBackgroundColored>
      </ListItem>

      <CustomConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDelete}
        title="Remover Produto"
        dialogContent="Tem certeza que deseja remover este produto do carrinho?"
        confirmText="Remover"
        cancelText="Cancelar"
      />
    </motion.div>
  );
};

export default ProductListItem;
